// @greenlight-spec: 11-observability.md#cost-management
// @greenlight-spec: 15-database-schema.md#agent_token_usage
/**
 * A priced mode the session total alone cannot tell apart, so the client
 * reports it per usage row. `standard` is what an unmarked row means.
 * - `fast` / `ultrafast`: the vendor's premium speed tiers.
 * - `long_prompt`: requests whose prompt is over the model's prompt-length
 *   threshold, on a model priced by prompt length. It combines with a speed
 *   tier where the vendor prices both.
 */
export const AGENT_USAGE_VARIANTS = [
    'standard',
    'fast',
    'ultrafast',
    'long_prompt',
    'fast_long_prompt',
    'ultrafast_long_prompt',
];
/** From the start: no earlier price exists for these rows. */
const LAUNCH = '1970-01-01';
function price(model, input, cacheWrite5m, cacheWrite1h, cacheRead, output, variant = 'standard') {
    return {
        model,
        variant,
        effectiveFrom: LAUNCH,
        usdPerMTok: {
            input,
            output,
            cache_write_5m: cacheWrite5m,
            cache_write_1h: cacheWrite1h,
            cache_read: cacheRead,
        },
    };
}
/**
 * OpenAI has one cache-write class, so it prices both of ours. A model whose
 * row lists no cache-write price is billed for a write as plain input.
 */
function openai(model, input, cachedInput, cacheWrite, output, variant = 'standard') {
    const write = cacheWrite ?? input;
    return price(model, input, write, write, cachedInput, output, variant);
}
/**
 * OpenAI's published API prices, USD per million tokens, from
 * https://developers.openai.com/api/docs/pricing as read on 2026-10-20
 * (Standard, Fast, and Ultrafast tiers; long context is over 272K input
 * tokens, shown under the page's Long context-length switch). Column order in
 * each row: input, cached input, cache writes, output — the page's own order.
 * GPT-5 and later only: the models Codex runs. A long-context price the page
 * does not list (fast gpt-5.5 and gpt-5.4, gpt-5.4-mini, gpt-5.4-nano) is
 * left out, so that usage reads unpriced.
 */
const OPENAI_MODEL_PRICES = [
    openai('gpt-6-astra', 10, 1, 12.5, 50),
    openai('gpt-6-astra', 20, 2, 25, 75, 'long_prompt'),
    openai('gpt-6-astra', 20, 2, 25, 100, 'fast'),
    openai('gpt-6-astra', 40, 4, 50, 150, 'fast_long_prompt'),
    openai('gpt-6-astra', 60, 6, 75, 300, 'ultrafast'),
    openai('gpt-6-astra', 120, 12, 150, 450, 'ultrafast_long_prompt'),
    openai('gpt-6.1-sol', 2, 0.1, 2.5, 10),
    openai('gpt-6.1-sol', 4, 0.2, 5, 15, 'long_prompt'),
    openai('gpt-6.1-sol', 4, 0.2, 5, 20, 'fast'),
    openai('gpt-6.1-sol', 8, 0.4, 10, 30, 'fast_long_prompt'),
    openai('gpt-6.1-sol', 12, 0.6, 15, 60, 'ultrafast'),
    openai('gpt-6.1-sol', 24, 1.2, 30, 90, 'ultrafast_long_prompt'),
    openai('gpt-6-luna', 0.1, 0.01, 0.125, 0.5),
    openai('gpt-6-luna', 0.2, 0.02, 0.25, 0.75, 'long_prompt'),
    openai('gpt-6-luna', 0.2, 0.02, 0.25, 1, 'fast'),
    openai('gpt-6-luna', 0.4, 0.04, 0.5, 1.5, 'fast_long_prompt'),
    openai('gpt-6-sol', 2, 0.2, 2.5, 10),
    openai('gpt-6-sol', 4, 0.4, 5, 20, 'fast'),
    openai('gpt-6-sol', 4, 0.4, 5, 15, 'long_prompt'),
    openai('gpt-6-sol', 8, 0.8, 10, 30, 'fast_long_prompt'),
    openai('gpt-5.6-sol', 4, 0.4, 5, 20),
    openai('gpt-5.6-sol', 8, 0.8, 10, 30, 'long_prompt'),
    openai('gpt-5.6-sol', 8, 0.8, 10, 40, 'fast'),
    openai('gpt-5.6-sol', 16, 1.6, 20, 60, 'fast_long_prompt'),
    openai('gpt-5.6-terra', 2, 0.2, 2.5, 12),
    openai('gpt-5.6-terra', 4, 0.4, 5, 24, 'fast'),
    openai('gpt-5.6-terra', 4, 0.4, 5, 18, 'long_prompt'),
    openai('gpt-5.6-terra', 8, 0.8, 10, 36, 'fast_long_prompt'),
    openai('gpt-5.6-luna', 0.2, 0.02, 0.25, 1.2),
    openai('gpt-5.6-luna', 0.4, 0.04, 0.5, 2.4, 'fast'),
    openai('gpt-5.6-luna', 0.4, 0.04, 0.5, 1.8, 'long_prompt'),
    openai('gpt-5.6-luna', 0.8, 0.08, 1, 3.6, 'fast_long_prompt'),
    openai('gpt-5.5', 5, 0.5, null, 30),
    openai('gpt-5.5', 12.5, 1.25, null, 75, 'fast'),
    openai('gpt-5.5', 10, 1, null, 45, 'long_prompt'),
    openai('gpt-5.4', 2.5, 0.25, null, 15),
    openai('gpt-5.4', 5, 0.5, null, 30, 'fast'),
    openai('gpt-5.4', 5, 0.5, null, 22.5, 'long_prompt'),
    openai('gpt-5.4-mini', 0.75, 0.075, null, 4.5),
    openai('gpt-5.4-mini', 1.5, 0.15, null, 9, 'fast'),
    openai('gpt-5.4-nano', 0.2, 0.02, null, 1.25),
    openai('gpt-5.3-codex', 1.75, 0.175, null, 14),
    openai('gpt-5.3-codex', 3.5, 0.35, null, 28, 'fast'),
    // Deprecated and off the pricing page; its model page still publishes this price.
    openai('gpt-5-codex', 1.25, 0.125, null, 10),
    openai('gpt-5.2', 1.75, 0.175, null, 14),
    openai('gpt-5.2', 3.5, 0.35, null, 28, 'fast'),
    openai('gpt-5.1', 1.25, 0.125, null, 10),
    openai('gpt-5.1', 2.5, 0.25, null, 20, 'fast'),
    openai('gpt-5', 1.25, 0.125, null, 10),
    openai('gpt-5', 2.5, 0.25, null, 20, 'fast'),
    openai('gpt-5-mini', 0.25, 0.025, null, 2),
    openai('gpt-5-mini', 0.45, 0.045, null, 3.6, 'fast'),
    openai('gpt-5-nano', 0.05, 0.005, null, 0.4),
];
/**
 * Anthropic's published API prices, USD per million tokens, from
 * https://platform.claude.com/docs/en/about-claude/pricing as read on
 * 2026-10-20 (Model pricing, Fast mode pricing, and Long context pricing).
 * Column order in each row: input, 5m cache write, 1h cache write, cache read,
 * output — the page's own order. Model ids are the Claude API ids from
 * https://platform.claude.com/docs/en/about-claude/model-deprecations.
 *
 * A vendor price change is a new row with a later `effectiveFrom`, never an
 * edit to an existing one: sessions keep the price in effect when they ended.
 * A model missing from this table is reported unpriced, never priced at a
 * guess. OpenAI models are in `OPENAI_MODEL_PRICES` below.
 */
export const AGENT_MODEL_PRICES = Object.freeze([
    price('claude-fable-5-1', 10, 12.5, 20, 0.25, 50),
    price('claude-mythos-5-1', 10, 12.5, 20, 0.25, 50),
    price('claude-fable-5', 10, 12.5, 20, 1, 50),
    price('claude-mythos-5', 10, 12.5, 20, 1, 50),
    price('claude-opus-5-5', 4, 5, 8, 0.2, 20),
    price('claude-opus-5', 5, 6.25, 10, 0.5, 25),
    price('claude-opus-4-8', 5, 6.25, 10, 0.5, 25),
    price('claude-opus-4-7', 5, 6.25, 10, 0.5, 25),
    price('claude-opus-4-6', 5, 6.25, 10, 0.5, 25),
    price('claude-opus-4-5', 5, 6.25, 10, 0.5, 25),
    price('claude-opus-4-1', 15, 18.75, 30, 1.5, 75),
    price('claude-opus-4', 15, 18.75, 30, 1.5, 75),
    price('claude-sonnet-5-5', 2, 2.5, 4, 0.1, 10),
    price('claude-sonnet-5', 2, 2.5, 4, 0.2, 10),
    price('claude-sonnet-4-6', 3, 3.75, 6, 0.3, 15),
    price('claude-sonnet-4-5', 3, 3.75, 6, 0.3, 15),
    price('claude-sonnet-4', 3, 3.75, 6, 0.3, 15),
    price('claude-haiku-5-5', 0.1, 0.125, 0.2, 0.01, 0.5),
    price('claude-haiku-5-5', 0.5, 0.625, 1, 0.05, 2.5, 'long_prompt'),
    price('claude-haiku-4-5', 1, 1.25, 2, 0.1, 5),
    price('claude-3-5-haiku', 0.8, 1, 1.6, 0.08, 4),
    // Fast mode lists input and output only; "prompt caching multipliers apply on
    // top of fast mode pricing", so the cache classes take the model's own
    // multipliers on the fast input price: 1.25x and 2x for writes, and for
    // reads 0.05x on Opus 5.5 and the standard 0.1x on Opus 5 and 4.8.
    price('claude-opus-5-5', 8, 10, 16, 0.4, 40, 'fast'),
    price('claude-opus-5', 10, 12.5, 20, 1, 50, 'fast'),
    price('claude-opus-4-8', 10, 12.5, 20, 1, 50, 'fast'),
    ...OPENAI_MODEL_PRICES,
]);
/**
 * The canonical id for a model string a client reported. Clients report the
 * id the session ran on, which may carry a cloud platform's spelling or a
 * snapshot date: `us.anthropic.claude-opus-4-8-v1:0` (Bedrock),
 * `claude-sonnet-4-5@20250929` (Google Cloud), `claude-sonnet-4-5-20250929`
 * (a dated snapshot), or `claude-opus-4-6[1m]` (Claude Code's context label).
 * All of these name the model priced as `claude-opus-4-8`, `claude-sonnet-4-5`,
 * and so on. The figure is an API-equivalent at Claude API prices whichever
 * platform served it.
 */
export function normalizeAgentModelId(model) {
    return model
        .trim()
        .toLowerCase()
        .replace(/^(?:[a-z]{2,6}\.)?anthropic\./, '')
        .replace(/\[[^\]]*\]$/, '')
        .replace(/-v\d+(?::\d+)?$/, '')
        .replace(/@\d{8}$/, '')
        .replace(/-\d{8}$/, '');
}
/**
 * The price in effect for `model` and `variant` when a session ended at
 * `endedAt`, or `undefined` when the table has none. A variant the model is
 * not priced for is unpriced too: a `fast` row on a model without fast mode is
 * not quietly priced at its standard rate.
 */
export function findAgentModelPrice(model, variant, endedAt, table = AGENT_MODEL_PRICES) {
    const canonical = normalizeAgentModelId(model);
    const endedDay = endedAt.toISOString().slice(0, 10);
    let found;
    for (const row of table) {
        if (row.model !== canonical || row.variant !== variant)
            continue;
        if (row.effectiveFrom > endedDay)
            continue;
        if (found === undefined || row.effectiveFrom > found.effectiveFrom)
            found = row;
    }
    return found;
}
/** USD for `counts` at `prices`, unrounded; each class at its own rate. */
export function priceAgentTokens(counts, prices) {
    return ((counts.input_tokens * prices.input +
        counts.output_tokens * prices.output +
        counts.cache_write_5m_tokens * prices.cache_write_5m +
        counts.cache_write_1h_tokens * prices.cache_write_1h +
        counts.cache_read_tokens * prices.cache_read) /
        1_000_000);
}
//# sourceMappingURL=agent-model-prices.js.map